import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

function wsStyle(w) {
  if (w.registration_type === 'free')
    return { bg: 'rgba(59,130,246,0.07)', border: 'rgba(59,130,246,0.22)', dot: '#3B82F6', badge: '#3B82F6', badgeBg: 'rgba(59,130,246,0.10)' };
  if (w.title && w.title.toLowerCase().includes('minigolf'))
    return { bg: 'rgba(45,106,94,0.07)',  border: 'rgba(45,106,94,0.22)',  dot: '#2D6A5E', badge: '#2D6A5E', badgeBg: 'rgba(45,106,94,0.12)' };
  return   { bg: 'rgba(168,117,43,0.07)', border: 'rgba(168,117,43,0.22)', dot: '#A8752B', badge: '#A8752B', badgeBg: 'rgba(168,117,43,0.12)' };
}

function OccupancyBar({ current, max }) {
  const pct = max > 0 ? Math.min(100, Math.round(current / max * 100)) : 0;
  const color = pct >= 90 ? '#E74C3C' : pct >= 70 ? '#A8752B' : '#27AE60';
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-[10px] text-manrique-muted">
        <span>{current} / {max}</span>
        <span style={{ color }}>{pct}%</span>
      </div>
      <div className="w-full h-1.5 rounded-full" style={{ background: 'rgba(0,0,0,0.08)' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export default function DailyCalendar({ api }) {
  const [date, setDate]   = useState(() => new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/workshops/by-date/${d}`);
      setItems(res.data.sort((a, b) => a.time_start.localeCompare(b.time_start)));
    } catch { setItems([]); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(date); }, [date, load]);

  useEffect(() => {
    const iv = setInterval(() => load(date), 30000);
    return () => clearInterval(iv);
  }, [date, load]);

  const shift = (d) => {
    const dt = new Date(date + 'T12:00:00');
    dt.setDate(dt.getDate() + d);
    setDate(dt.toISOString().split('T')[0]);
  };

  const morning   = items.filter(w => w.time_slot === 'morning');
  const afternoon = items.filter(w => w.time_slot === 'afternoon');

  function WsCard({ w }) {
    const st    = wsStyle(w);
    const isFree = w.registration_type === 'free';
    const unit   = w.capacity_unit === 'palos' ? 'palos' : 'plazas';
    return (
      <div className="rounded-2xl border p-4 space-y-2.5" style={{ background: st.bg, borderColor: st.border }}>
        <div className="flex items-start gap-2">
          <div className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: st.dot }} />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm text-manrique-black leading-tight">{w.title}</p>
            {w.pool_zone && <p className="text-[10px] text-manrique-muted mt-0.5">{w.pool_zone}</p>}
          </div>
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ background: st.badgeBg, color: st.badge }}
          >
            {isFree ? 'LIBRE' : 'RESERVA'}
          </span>
        </div>
        <p className="text-xs font-mono text-manrique-grey">{w.time_start}–{w.time_end}</p>
        {!isFree && w.max_capacity < 999 && (
          <OccupancyBar current={w.current_count} max={w.max_capacity} />
        )}
        {isFree && (
          <p className="text-[10px] text-manrique-muted">
            {w.age_min === 0 && w.age_max >= 99 ? 'Todas las edades' : `${w.age_min}–${w.age_max} años`}
          </p>
        )}
      </div>
    );
  }

  function Section({ label, list }) {
    if (!list.length) return null;
    return (
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-manrique-muted mb-3">{label}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map(w => <WsCard key={w.id} w={w} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Navigator */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => shift(-1)}
          className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FBF8F3] transition-colors"
          data-testid="dcal-prev"
        >
          <ChevronLeft size={16} />
        </button>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="input-manrique text-sm font-mono flex-shrink-0 w-44"
          data-testid="dcal-date"
        />
        <button
          onClick={() => shift(1)}
          className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FBF8F3] transition-colors"
          data-testid="dcal-next"
        >
          <ChevronRight size={16} />
        </button>
        <span className="text-sm text-manrique-muted ml-1">
          {items.length} actividad{items.length !== 1 ? 'es' : ''}
        </span>
        {loading && <div className="w-4 h-4 border-2 border-[#48293A] border-t-transparent rounded-full animate-spin" />}
        <div className="flex items-center gap-4 ml-auto flex-wrap">
          {[
            { dot: '#A8752B', label: 'El Club de Manri' },
            { dot: '#2D6A5E', label: 'Minigolf' },
            { dot: '#3B82F6', label: 'Libre' },
          ].map(l => (
            <div key={l.label} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: l.dot }} />
              <span className="text-[10px] text-manrique-muted">{l.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      {!loading && items.length === 0 ? (
        <div className="card-manrique text-center py-12">
          <Calendar size={28} className="mx-auto mb-3 text-manrique-muted opacity-30" />
          <p className="text-sm text-manrique-muted">No hay actividades para esta fecha</p>
        </div>
      ) : (
        <div className="space-y-6">
          <Section label="Mañana" list={morning} />
          <Section label="Tarde"  list={afternoon} />
        </div>
      )}
    </div>
  );
}
